import {
  Button,
  DialogActions,
  DialogContent,
  Stack,
  Typography,
} from '@mui/material';
import Dialog from 'components/Dialog';
import Switch from 'components/Switch';
import {
  betaFeatures,
  isBetaFeatureEnabled,
  setBetaFeatureEnabled,
} from 'hooks/useFeatureFlag';
import { useEffect, useState } from 'react';

interface BetaFeaturesDialogProps {
  open: boolean;
  onClose: () => void;
}

const BetaFeaturesDialog = ({ open, onClose }: BetaFeaturesDialogProps) => {
  const [features, setFeatures] = useState(() =>
    Object.fromEntries(
      betaFeatures.map(({ flag }) => [flag, isBetaFeatureEnabled(flag)]),
    ),
  );

  useEffect(() => {
    if (open) {
      setFeatures(
        Object.fromEntries(
          betaFeatures.map(({ flag }) => [flag, isBetaFeatureEnabled(flag)]),
        ),
      );
    }
  }, [open]);

  const handleToggle = (flag: (typeof betaFeatures)[number]['flag']) => {
    const enabled = !features[flag];
    setFeatures((currentFeatures) => ({
      ...currentFeatures,
      [flag]: enabled,
    }));
    setBetaFeatureEnabled(flag, enabled);
  };

  const handleReset = () => {
    betaFeatures.forEach(({ flag }) => setBetaFeatureEnabled(flag, false));
    setFeatures({});
  };

  return (
    <Dialog
      title='Activated beta features'
      open={open}
      onClose={onClose}
      closeButton
      maxWidth='xs'
    >
      <DialogContent>
        <Stack
          sx={{
            gap: 1,
          }}
        >
          {betaFeatures.map(({ flag, label, description }) => (
            <Stack
              key={flag}
              direction='row'
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <Stack>
                <Typography>{label}</Typography>
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {description}
                </Typography>
              </Stack>
              <Switch
                checked={features[flag] ?? false}
                onChange={() => handleToggle(flag)}
                slotProps={{ input: { 'aria-label': `Enable ${label}` } }}
              />
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset}>Reset</Button>
        <Button onClick={onClose} variant='contained'>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BetaFeaturesDialog;
